import React, { useCallback, useEffect, useState } from 'react';
import axios from "axios";
import { toast } from 'react-toastify';
import Select from "../../components/select";

import styles from './Activity.module.scss';
import Spinner from '../../components/spinner';
import Pagination from '../../components/pagination';
import { useLanguage } from '../../context/languageContext';

const API = process.env.REACT_APP_API_ROOT;

/**
 * Activity — who did what, across every content area.
 *
 * Fed by `GET /admin/audit-logs`, which is written by `requirePermission` on
 * every non-view action. Owner-only: the route is gated on `admins.view`, the
 * same right that guards the Team page.
 *
 * Filtering happens server-side (the endpoint paginates and filters), so this
 * page holds no derived state beyond the current query.
 */

const ACTION_LABELS = {
    create: "Added",
    edit: "Edited",
    delete: "Deleted",
    publish: "Published",
};

const ACTION_CLASS = {
    create: styles.badgeCreate,
    edit: styles.badgeEdit,
    delete: styles.badgeDelete,
    publish: styles.badgePublish,
};

const formatWhen = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

function Activity() {
    const { adminInfo } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [pageInfo, setPageInfo] = useState({ total: 0, page: 1, pages: 1, limit: 25 });
    const [currentPage, setCurrentPage] = useState(1);

    const [admins, setAdmins] = useState([]);
    const [features, setFeatures] = useState([]);
    const [filters, setFilters] = useState({ adminId: "", feature: "", action: "", from: "", to: "" });

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: currentPage, limit: 25 };
            // Blank values must not be sent — the API treats a present `adminId`
            // as a filter even when it's an empty string.
            for (const [key, value] of Object.entries(filters)) {
                if (value) params[key] = value;
            }

            const { data: response } = await axios.get(`${API}/api/admin/audit-logs`, {
                headers: { Authorization: `Bearer ${adminInfo.token}` },
                params,
            });

            if (response.status) {
                setLogs(response.data?.items || []);
                setPageInfo({
                    total: response.data?.total || 0,
                    page: response.data?.page || 1,
                    pages: response.data?.pages || 1,
                    limit: response.data?.limit || 25,
                });
            } else {
                toast.error(response.message || "Could not load activity.");
            }
        } catch (error) {
            console.error("fetchLogs catch error::", error);
            toast.error(error.response?.data?.error || "Could not load activity.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo, currentPage, filters]);

    // The person and feature pickers come from the API rather than a hardcoded
    // list, so a new admin or a new feature shows up without a code change.
    const fetchFilterOptions = useCallback(async () => {
        try {
            const [adminsRes, catalogRes] = await Promise.all([
                axios.get(`${API}/api/admin/admins`, { headers: { Authorization: `Bearer ${adminInfo.token}` } }),
                axios.get(`${API}/api/admin/access/catalog`, { headers: { Authorization: `Bearer ${adminInfo.token}` } }),
            ]);

            if (adminsRes.data?.status) {
                setAdmins((adminsRes.data.data || []).map((a) => ({
                    value: a._id,
                    label: `${a.userName || a.email}${a.role ? ` · ${a.role}` : ""}`,
                })));
            }
            if (catalogRes.data?.status) {
                const catalogFeatures = catalogRes.data.data?.features || [];
                setFeatures(catalogFeatures.map((f) => ({
                    value: f.key ?? f,
                    label: f.label ?? f,
                })));
            }
        } catch (error) {
            console.error("fetchFilterOptions catch error::", error);
        }
    }, [adminInfo]);

    useEffect(() => {
        if (adminInfo?.token) fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    useEffect(() => {
        if (adminInfo?.token) fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo, currentPage, filters]);

    const updateFilter = (key, value) => {
        // Any filter change invalidates the page number — staying on page 4 of a
        // narrower result set shows an empty table that looks like "no activity".
        setCurrentPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const hasFilters = Object.values(filters).some(Boolean);

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">Activity{pageInfo.total > 0 ? ` (${pageInfo.total})` : ""}</h4>
                    <small className="text-muted">
                        Every add, edit, delete and publish — who did it, and to what.
                    </small>
                </div>
                {hasFilters && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setCurrentPage(1);
                            setFilters({ adminId: "", feature: "", action: "", from: "", to: "" });
                        }}
                    >
                        Clear filters
                    </button>
                )}
            </div>

            <div className={styles.filterBar}>
                <div className={styles.filterField}>
                    <label className={styles.filterLabel}>Person</label>
                    <Select
                        isClearable
                        placeholder="Anyone"
                        options={admins}
                        value={admins.find((o) => o.value === filters.adminId) || null}
                        onChange={(o) => updateFilter("adminId", o?.value || "")}
                    />
                </div>
                <div className={styles.filterField}>
                    <label className={styles.filterLabel}>Area</label>
                    <Select
                        isClearable
                        placeholder="All areas"
                        options={features}
                        value={features.find((o) => o.value === filters.feature) || null}
                        onChange={(o) => updateFilter("feature", o?.value || "")}
                    />
                </div>
                <div className={styles.filterField}>
                    <label className={styles.filterLabel}>Action</label>
                    <Select
                        isClearable
                        placeholder="All actions"
                        options={Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label }))}
                        value={
                            filters.action
                                ? { value: filters.action, label: ACTION_LABELS[filters.action] }
                                : null
                        }
                        onChange={(o) => updateFilter("action", o?.value || "")}
                    />
                </div>
                {/* Neither picker bounds the other: greying out half the
                    calendar meant a new range could not be picked without
                    clearing the old one first. A reversed pair is swapped
                    server-side (see listAuditLogs). */}
                <div className={styles.filterField}>
                    <label className={styles.filterLabel}>From</label>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.from}
                        onChange={(e) => updateFilter("from", e.target.value)}
                    />
                </div>
                <div className={styles.filterField}>
                    <label className={styles.filterLabel}>To</label>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filters.to}
                        onChange={(e) => updateFilter("to", e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <React.Fragment>
                    <div className={`${styles.container} table-responsive mt-1`}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.when}>When</th>
                                    <th>Who</th>
                                    <th className={styles.action}>Action</th>
                                    <th>Area</th>
                                    <th>Target</th>
                                    <th className={styles.ip}>IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className={styles.empty}>
                                            {hasFilters
                                                ? "No activity matches these filters."
                                                : "No activity recorded yet."}
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log._id}>
                                            <td className={styles.when}>{formatWhen(log.createdAt)}</td>
                                            <td>
                                                <span className={styles.who}>{log.adminName || "—"}</span>
                                                {log.adminEmail && (
                                                    <span className={styles.whoEmail}>{log.adminEmail}</span>
                                                )}
                                            </td>
                                            <td className={styles.action}>
                                                <span className={`${styles.badge} ${ACTION_CLASS[log.action] || ""}`}>
                                                    {ACTION_LABELS[log.action] || log.action}
                                                </span>
                                            </td>
                                            <td className={styles.feature}>{log.feature}</td>
                                            <td className={styles.target} title={log.targetLabel || ""}>
                                                {log.targetLabel || <span className={styles.none}>—</span>}
                                            </td>
                                            <td className={styles.ip}>{log.ip || "—"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {pageInfo.pages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={pageInfo.pages}
                            totalItems={pageInfo.total}
                            itemsPerPage={pageInfo.limit}
                            onPageChange={setCurrentPage}
                            // Page size is fixed at 25 — there's no state behind
                            // the selector, so showing it would be a dead control.
                            showItemsPerPage={false}
                        />
                    )}
                </React.Fragment>
            )}
        </React.Fragment>
    );
}

export default Activity;
